// Prepared, immutable view of a MinimapSnapshot: O(1) tile lookup, bounds, a terrain signature
// (biome layout + which tiles are still fog) used to key the cached static layer, and the
// per-layer tile lists the painter iterates (so a repaint does no per-tile classification).
// The sim already blanks the biome of fog tiles, so exploring a tile changes the signature.

import { type Bounds, EDGE_NEIGHBORS, boardBounds, tileKey } from './minimapGeometry.js';
import type { MinimapSnapshot, MinimapTile } from './types.js';

export interface BorderTile {
  readonly tile: MinimapTile;
  /** Bit e set = edge e (see EDGE_NEIGHBORS) borders a different owner. */
  readonly mask: number;
}

export interface OwnerLayer {
  readonly owner: number;
  /** Non-fog tiles controlled by `owner`. */
  readonly tiles: readonly MinimapTile[];
  /** Subset of `tiles` with at least one edge towards a different owner. */
  readonly borders: readonly BorderTile[];
}

export interface PreparedBoard {
  readonly tiles: readonly MinimapTile[];
  readonly byKey: ReadonlyMap<number, MinimapTile>;
  readonly bounds: Bounds;
  /** Changes only when the set of tiles or any biome changes (not on fog/owners/units). */
  readonly terrainSignature: string;
  /** Territory per owner (ascending owner id). */
  readonly owners: readonly OwnerLayer[];
  /** Explored (dimmed) tiles. */
  readonly explored: readonly MinimapTile[];
  /** Non-fog tiles with a city or a unit. */
  readonly icons: readonly MinimapTile[];
}

export function terrainSignature(tiles: readonly MinimapTile[]): string {
  // FNV-1a over (q, r, fog?, biome); cheap enough for thousands of tiles.
  let h = 0x811c9dc5;
  const mix = (n: number) => {
    h ^= n;
    h = Math.imul(h, 0x01000193);
  };
  for (const t of tiles) {
    mix(t.q & 0xffff);
    mix(t.r & 0xffff);
    mix(t.visibility === 'fog' ? 1 : 2);
    for (let i = 0; i < t.biome.length; i++) mix(t.biome.charCodeAt(i));
    // Rivers and terrain levels are drawn on the static layer, so they must
    // invalidate it (bridges change `riverEdges`; they are a subset and stay
    // under the river bit).
    mix((t.riverEdges ?? 0) & 0xffff);
    mix((t.terrainLevel ?? 0) & 0xffff);
  }
  return `${tiles.length}:${(h >>> 0).toString(36)}`;
}

/**
 * Owner of a tile for border purposes.
 *
 * A fog tile's owner is unknown to the player, so it counts as *no owner* —
 * never as player 0, which would leak the human's ownership into the fog.
 * `null` therefore differs from every real player id, 0 included.
 */
function ownerOf(t: MinimapTile | undefined): number | null {
  return t && t.visibility !== 'fog' ? (t.controlledBy ?? null) : null;
}

export function prepareBoard(snapshot: MinimapSnapshot): PreparedBoard {
  const byKey = new Map<number, MinimapTile>();
  for (const t of snapshot.tiles) byKey.set(tileKey(t.q, t.r), t);

  const groups = new Map<number, { tiles: MinimapTile[]; borders: BorderTile[] }>();
  const explored: MinimapTile[] = [];
  const icons: MinimapTile[] = [];
  for (const t of snapshot.tiles) {
    if (t.visibility === 'fog') continue;
    if (t.visibility === 'explored') explored.push(t);
    if (t.hasCity || t.hasUnit) icons.push(t);
    // Unclaimed land has no owner; every controlled tile does — including the
    // human's (owner id 0).
    const owner = t.controlledBy ?? null;
    if (owner === null) continue;
    let g = groups.get(owner);
    if (!g) {
      g = { tiles: [], borders: [] };
      groups.set(owner, g);
    }
    g.tiles.push(t);
    let mask = 0;
    for (let e = 0; e < 6; e++) {
      const d = EDGE_NEIGHBORS[e] ?? [0, 0];
      if (ownerOf(byKey.get(tileKey(t.q + d[0], t.r + d[1]))) !== owner) mask |= 1 << e;
    }
    if (mask !== 0) g.borders.push({ tile: t, mask });
  }
  const owners = [...groups.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([owner, g]) => ({ owner, tiles: g.tiles, borders: g.borders }));

  return {
    tiles: snapshot.tiles,
    byKey,
    bounds: boardBounds(snapshot.tiles),
    terrainSignature: terrainSignature(snapshot.tiles),
    owners,
    explored,
    icons,
  };
}

export function tileAt(board: PreparedBoard, q: number, r: number): MinimapTile | undefined {
  return board.byKey.get(tileKey(q, r));
}
